/**
 * Wall-device lock helpers.
 *
 * Wall Mode uses its own wall-only session token. The wall device should
 * never keep a parent auth session; from the browser's perspective it is
 * similar to Kid-Only Mode, but scoped to the shared wall dashboard.
 *
 * The device is also registered server-side in `wall_devices` so the
 * parent can revoke it remotely from their own phone. The wall page
 * polls `wall-device-status` periodically; if the row is revoked we
 * clear local state and sign the user out.
 */

import { supabase } from '@/integrations/supabase/client';

const KEY_LOCKED = 'wall_device_locked';
const KEY_DEVICE_ID = 'wall_device_id';
const KEY_FAMILY_ID = 'wall_device_family_id';
const KEY_LABEL = 'wall_device_label';
const KEY_SESSION_TOKEN = 'wall_session_token';

export function isWallDeviceLocked(): boolean {
  try { return localStorage.getItem(KEY_LOCKED) === '1'; } catch { return false; }
}

export function getWallDeviceId(): string | null {
  try { return localStorage.getItem(KEY_DEVICE_ID); } catch { return null; }
}

export function getWallDeviceFamilyId(): string | null {
  try { return localStorage.getItem(KEY_FAMILY_ID); } catch { return null; }
}

export function getWallDeviceLabel(): string | null {
  try { return localStorage.getItem(KEY_LABEL); } catch { return null; }
}

export function getWallSessionToken(): string | null {
  try { return localStorage.getItem(KEY_SESSION_TOKEN); } catch { return null; }
}

export function hasWallSession(): boolean {
  return !!getWallSessionToken() && !!getWallDeviceId();
}

export function setWallDeviceLock(opts: { deviceId: string; familyId: string; label: string; sessionToken?: string }) {
  try {
    localStorage.setItem(KEY_LOCKED, '1');
    localStorage.setItem(KEY_DEVICE_ID, opts.deviceId);
    localStorage.setItem(KEY_FAMILY_ID, opts.familyId);
    localStorage.setItem(KEY_LABEL, opts.label);
    if (opts.sessionToken) localStorage.setItem(KEY_SESSION_TOKEN, opts.sessionToken);
  } catch { /* */ }
}

export function clearWallDeviceLock() {
  try {
    localStorage.removeItem(KEY_LOCKED);
    localStorage.removeItem(KEY_DEVICE_ID);
    localStorage.removeItem(KEY_FAMILY_ID);
    localStorage.removeItem(KEY_LABEL);
    localStorage.removeItem(KEY_SESSION_TOKEN);
  } catch { /* */ }
}

/**
 * Poll the server for revocation. Returns 'revoked' | 'active' | 'not_found' | 'invalid' | 'error'.
 */
export async function checkWallDeviceStatus(): Promise<string> {
  const deviceId = getWallDeviceId();
  if (!deviceId) return 'invalid';
  try {
    const { data, error } = await supabase.functions.invoke('wall-device-status', {
      body: { device_id: deviceId },
      headers: getWallSessionToken() ? { 'x-wall-session-token': getWallSessionToken()! } : undefined,
    });
    if (error) return 'error';
    return (data?.status as string) || 'error';
  } catch {
    return 'error';
  }
}
